/**
 * The Share panel, from the footer next to Settings: links that open the
 * usual platforms' share dialogs with the site link and a line about the
 * player's eggs, plus copy-link and (where the browser offers it) the
 * device's own share sheet. Links are plain share-intent URLs; nothing is
 * loaded from the platforms.
 */

import { formatEggs } from '../game/numbers';

export const SITE_URL = 'https://dinoclicker.com/';

export interface ShareStats {
  /** Lifetime eggs, for the message. */
  totalEggs: number;
  /** Eras ended; 0 before the first extinction. */
  era: number;
  /** Fossils the last era paid (the message says "dug out of the last one"). */
  fossils: number;
}

/** The line that goes with the link. */
export function shareText(stats: ShareStats): string {
  const eggs = formatEggs(stats.totalEggs);
  // Once the world has ended once, that is the more interesting number.
  if (stats.era > 0) {
    const fossils = `${formatEggs(stats.fossils)} fossil${stats.fossils === 1 ? '' : 's'}`;
    return `Era ${stats.era + 1} in DinoClicker, ${fossils} dug out of the last one and ${eggs} eggs laid all told.`;
  }
  return stats.totalEggs >= 1000
    ? `My dinosaurs have laid ${eggs} eggs in DinoClicker. Click the egg, raise the dinos, collect the eggs.`
    : 'DinoClicker: click the egg, raise the dinos, collect the eggs.';
}

interface Platform {
  name: string;
  url(text: string, link: string): string;
}

/** Share-intent URLs; each platform takes the text and link differently. */
export const PLATFORMS: readonly Platform[] = [
  {
    name: 'X',
    url: (text, link) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`,
  },
  {
    name: 'Facebook',
    url: (text, link) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}&quote=${encodeURIComponent(text)}`,
  },
  {
    name: 'Reddit',
    url: (text, link) =>
      `https://www.reddit.com/submit?url=${encodeURIComponent(link)}&title=${encodeURIComponent(text)}`,
  },
  {
    name: 'Bluesky',
    url: (text, link) =>
      `https://bsky.app/intent/compose?text=${encodeURIComponent(`${text} ${link}`)}`,
  },
  {
    name: 'WhatsApp',
    url: (text, link) => `https://wa.me/?text=${encodeURIComponent(`${text} ${link}`)}`,
  },
  {
    name: 'Email',
    url: (text, link) =>
      `mailto:?subject=${encodeURIComponent('DinoClicker')}&body=${encodeURIComponent(`${text}\n\n${link}`)}`,
  },
];

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * `onShare` runs when a link is used: a platform link opened, the link
 * copied, or the device's share sheet completed.
 */
export function createShareView(stats: () => ShareStats, onShare?: () => void): HTMLElement {
  const body = el('div', 'settings__body share');
  body.append(el('p', 'share__blurb', 'Tell someone about the eggs.'));

  const links = el('div', 'share__links');
  for (const platform of PLATFORMS) {
    const a = el('a', 'settings__button', platform.name);
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    // The message is composed when used, so the egg count is current: on
    // pointerdown for a middle-click or "open in new tab", and on click,
    // which runs before the link is followed, for a keyboard Enter.
    const compose = (): void => {
      a.href = platform.url(shareText(stats()), SITE_URL);
    };
    a.addEventListener('pointerdown', compose);
    a.addEventListener('click', () => {
      compose();
      onShare?.();
    });
    a.href = platform.url(shareText(stats()), SITE_URL);
    links.append(a);
  }

  const copy = el('button', 'settings__button', 'Copy link');
  copy.type = 'button';
  copy.addEventListener('click', () => {
    const done = (): void => {
      onShare?.();
      copy.textContent = 'Copied';
      window.setTimeout(() => (copy.textContent = 'Copy link'), 1500);
    };
    navigator.clipboard
      ?.writeText(`${shareText(stats())} ${SITE_URL}`)
      .then(done)
      .catch(() => {
        copy.textContent = 'Copy failed';
      });
  });
  links.append(copy);

  // The device's own share sheet, where the browser offers one (phones, mostly).
  if (typeof navigator.share === 'function') {
    const native = el('button', 'settings__button', 'Share…');
    native.type = 'button';
    native.addEventListener('click', () => {
      void navigator
        .share({ title: 'DinoClicker', text: shareText(stats()), url: SITE_URL })
        .then(() => onShare?.())
        .catch(() => {
          // Dismissed; nothing to do.
        });
    });
    links.append(native);
  }

  body.append(links);
  return body;
}
